import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as amplitude from "@amplitude/analytics-browser";
import {
  Input,
  Toggle,
  Button,
  SkeletonBase,
} from '@unified-checkout/design-system';
import {
  Pen,
  Plus,
  ArrowUpRightFromSquare,
} from '@unified-checkout/design-system/icons/index';
import {
  getBranchHandles,
  createBranchHandle,
  updateBranchHandle,
} from '@app/dashboard/services/inner_layout/payme';
import { notify } from '@app/dashboard/utils/notifier';
import { getPayMeErrorMessage } from '@app/dashboard/views/pages/inner_layout/PayMe/utils/errorMessage';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { PAYME_BASE_URL } from '@app/dashboard/constants/payme-links';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { PROFILE } from '@app/dashboard/constants/profile';
import { PAYME_EVENTS } from '@app/dashboard/constants/tracking_events/payme';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { getProfileType } from '@app/dashboard/utils/localStorageUtils';
import CopyText from '@app/dashboard/components/inner_layout/CopyText';
import { PayMeShareModal } from '../PayMeShareModal';
import { BranchNameModal } from '../BranchNameModal';
import { DeactivateConfirmModal } from '../DeactivateConfirmModal';
import type { IBranchHandle, IPaymentHandleConfig } from '@app/dashboard/types/innerLayout/payme';
import styles from './branch-links-card.module.scss';

const NOTIFICATION_MAX_WIDTH = "32rem";

export interface Branch {
  id: number;
  name: string;
  isActive: boolean;
}

export interface BranchLinksCardProps {
  handle: string;
  branches?: IBranchHandle[];
  isMainHandleActivated?: boolean;
  isPayMeConfigLoading?: boolean;
  logoUrl?: string | null;
}

export const BranchLinksCard = ({
  handle,
  branches: branchesProp,
  isMainHandleActivated = true,
  isPayMeConfigLoading = false,
  logoUrl,
}: BranchLinksCardProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const isStaff = getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;
  const isMerchant = getProfileType() === PROFILE.TYPE.MERCHANT;
  const canAddBranchHandle = isStaff ? isAccessible(PERMISSIONS.CAN_ADD_BRANCH_HANDLE) : isMerchant;
  const canChangeBranchHandle = isStaff ? isAccessible(PERMISSIONS.CAN_CHANGE_BRANCH_HANDLE) : isMerchant;
  const canSharePaymentHandle = isStaff ? isAccessible(PERMISSIONS.CAN_SHARE_PAYMENT_HANDLE) : isMerchant;

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isBranchNameModalOpen, setIsBranchNameModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [branchToDeactivate, setBranchToDeactivate] = useState<number | null>(
    null
  );
  // Local state for branch names to allow editing without immediate API calls
  const [branchNames, setBranchNames] = useState<Record<number, string>>({});
  // Track which branch is currently being updated (for loading states)
  const [updatingBranchId, setUpdatingBranchId] = useState<number | null>(null);
  const [updatingStatusBranchIds, setUpdatingStatusBranchIds] = useState<
    Set<number>
  >(new Set());
  // Store original values before mutation to reset on error
  const [originalBranchName, setOriginalBranchName] = useState<
    Record<number, string>
  >({});
  const [originalBranchStatus, setOriginalBranchStatus] = useState<
    Record<number, boolean>
  >({});
  // Track if branches have been initialized to prevent initial onChange events
  const branchesInitializedRef = useRef(false);
  // Track toggle versions to force rerender on error
  const [toggleVersions, setToggleVersions] = useState<Record<number, number>>(
    {}
  );
  const [branchNameApiError, setBranchNameApiError] = useState<string | null>(
    null
  );

  // Fetch branch handles from API only if not provided as prop
  const {
    data: branchHandlesData,
    isLoading: isLoadingBranches,
    isError: isErrorBranches,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BRANCH_HANDLES],
    queryFn: () => getBranchHandles().then((res) => res.data),
    enabled: !branchesProp, // Only fetch if branches are not provided
  });

  // Map API response to component format
  // Use prop branches if provided, otherwise use fetched data
  const branches: Branch[] = branchesProp
    ? branchesProp.map((branch) => ({
      id: branch.id,
      name: branch.name,
      isActive: branch.is_active,
    }))
    : branchHandlesData?.map((branch: IBranchHandle) => ({
      id: branch.id,
      name: branch.name,
      isActive: branch.is_active,
    })) || [];

  // Initialize branch names from API data or prop
  useEffect(() => {
    const dataToUse = branchesProp || branchHandlesData;
    if (dataToUse && !isLoadingBranches) {
      const names: Record<number, string> = {};
      dataToUse.forEach((branch: IBranchHandle) => {
        names[branch.id] = branch.name;
      });
      setBranchNames(names);
      // Initialize toggle versions for new branches only
      setToggleVersions((prev) => {
        const updated = { ...prev };
        dataToUse.forEach((branch: IBranchHandle) => {
          if (updated[branch.id] === undefined) {
            updated[branch.id] = 0;
          }
        });
        return updated;
      });
      // Mark as initialized after data is loaded and components have rendered
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        branchesInitializedRef.current = true;
      });
    }
  }, [branchesProp, branchHandlesData, isLoadingBranches]);

  // Create branch mutation
  const createBranchMutation = useMutation({
    mutationFn: (name: string) => createBranchHandle({ name }),
    onSuccess: (_, branchName) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRANCH_HANDLES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });
      setBranchNameApiError(null);
      notify.success(t('innerLayout.paymePage.branchLinks.addModal.success'), {
        maxWidth: NOTIFICATION_MAX_WIDTH,
      })
      
      const currentBranches = branches.length;
      amplitude.track(PAYME_EVENTS.PAYME_BRANCH_ADDITION_SUCCEEDED, {
        branch_name: branchName,
        no_of_branches: currentBranches + 1,
      });
      
      // Close modal after a short delay
      setTimeout(() => {
        setIsBranchNameModalOpen(false);
      }, 2000);
    },
    onError: (error: any) => {
      const errorMessage = getPayMeErrorMessage(error, t);

      setBranchNameApiError(errorMessage);
      notify.error(errorMessage, {
        maxWidth: NOTIFICATION_MAX_WIDTH,
      });

      amplitude.track(PAYME_EVENTS.PAYME_BRANCH_ADDITION_FAILED, {
        error_description: errorMessage,
      });
    },
  });

  // Update branch name mutation
  const updateBranchNameMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => {
      // Store original name before mutation
      const branch = branches.find((b) => b.id === id);
      if (branch) {
        setOriginalBranchName((prev) => ({
          ...prev,
          [id]: branch.name,
        }));
      }
      setUpdatingBranchId(id);
      return updateBranchHandle({ id, name });
    },
    onSuccess: (_, variables) => {
      // Optimistically update the cache
      queryClient.setQueryData<IPaymentHandleConfig | null>(
        [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
        (oldData) => {
          if (!oldData || !oldData.branches) {
            return oldData;
          }
          return {
            ...oldData,
            branches: oldData.branches.map((branch: IBranchHandle) =>
              branch.id === variables.id
                ? { ...branch, name: variables.name }
                : branch
            ),
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRANCH_HANDLES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });
      setUpdatingBranchId(null);
      setOriginalBranchName({}); // Clear stored original name
      setBranchNameApiError(null); // Clear API error on success
      notify.success(t('innerLayout.paymePage.branchLinks.editModal.success'), { maxWidth: NOTIFICATION_MAX_WIDTH })
      setIsBranchNameModalOpen(false);
      setEditingBranch(null);
    },
    onError: (error: any, variables) => {
      setUpdatingBranchId(null);
      const errorMessage = getPayMeErrorMessage(error, t);

      setBranchNameApiError(errorMessage);

      // Revert optimistic cache update on error
      const originalName = originalBranchName[variables.id];
      if (originalName !== undefined) {
        queryClient.setQueryData<IPaymentHandleConfig | null>(
          [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
          (oldData) => {
            if (!oldData || !oldData.branches) {
              return oldData;
            }
            return {
              ...oldData,
              branches: oldData.branches.map((branch: IBranchHandle) =>
                branch.id === variables.id
                  ? { ...branch, name: originalName }
                  : branch
              ),
            };
          }
        );

        setBranchNames((prev) => ({
          ...prev,
          [variables.id]: originalName,
        }));
        // Reset editingBranch to original name so modal input resets
        if (editingBranch && editingBranch.id === variables.id) {
          setEditingBranch({
            ...editingBranch,
            name: originalName,
          });
        }
        // Clear stored original name
        setOriginalBranchName((prev) => {
          const updated = { ...prev };
          delete updated[variables.id];
          return updated;
        });
      }
      notify.error(errorMessage, { maxWidth: NOTIFICATION_MAX_WIDTH });
    },
  });

  // Update branch status mutation
  const updateBranchStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) => {
      // Store original status before mutation
      const branch = branches.find((b) => b.id === id);
      if (branch) {
        setOriginalBranchStatus((prev) => ({
          ...prev,
          [id]: branch.isActive,
        }));
      }

      // Add branch ID to updating set
      setUpdatingStatusBranchIds((prev) => new Set(prev).add(id));

      // Optimistically update branch status in GET_BRANCH_HANDLES cache
      queryClient.setQueryData<IBranchHandle[]>(
        [QUERY_KEYS.GET_BRANCH_HANDLES],
        (oldData) => {
          if (!oldData) return oldData;
          return oldData.map((branch) =>
            branch.id === id ? { ...branch, is_active: isActive } : branch
          );
        }
      );

      // Optimistically update branch status in GET_PAYMENT_HANDLE_CONFIG cache
      queryClient.setQueryData<IPaymentHandleConfig | null>(
        [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
        (oldData) => {
          if (!oldData || !oldData.branches) return oldData;
          return {
            ...oldData,
            branches: oldData.branches.map((branch: IBranchHandle) =>
              branch.id === id ? { ...branch, is_active: isActive } : branch
            ),
          };
        }
      );

      return updateBranchHandle({ id, is_active: isActive });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_BRANCH_HANDLES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
      });
      // Remove branch ID from updating set
      setUpdatingStatusBranchIds((prev) => {
        const updated = new Set(prev);
        updated.delete(variables.id);
        return updated;
      });
      // Show success notification
      const statusText = variables.isActive
        ? t('innerLayout.paymePage.branchLinks.statusUpdate.activated')
        : t('innerLayout.paymePage.branchLinks.statusUpdate.deactivated');

      notify.success(t(
          'innerLayout.paymePage.branchLinks.statusUpdate.success',
          { status: statusText }
        ), { maxWidth: NOTIFICATION_MAX_WIDTH })
    },
    onError: (error: any, variables) => {
      // Remove branch ID from updating set
      setUpdatingStatusBranchIds((prev) => {
        const updated = new Set(prev);
        updated.delete(variables.id);
        return updated;
      });
      // Get the original status before clearing it
      const originalStatus = originalBranchStatus[variables.id];

      // Immediately reset branch status in cache to original value
      if (originalStatus !== undefined) {
        // Revert GET_BRANCH_HANDLES cache
        queryClient.setQueryData<IBranchHandle[]>(
          [QUERY_KEYS.GET_BRANCH_HANDLES],
          (oldData) => {
            if (!oldData) return oldData;
            return oldData.map((branch) =>
              branch.id === variables.id
                ? { ...branch, is_active: originalStatus }
                : branch
            );
          }
        );

        // Revert GET_PAYMENT_HANDLE_CONFIG cache
        queryClient.setQueryData<IPaymentHandleConfig | null>(
          [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
          (oldData) => {
            if (!oldData || !oldData.branches) return oldData;
            return {
              ...oldData,
              branches: oldData.branches.map((branch: IBranchHandle) =>
                branch.id === variables.id
                  ? { ...branch, is_active: originalStatus }
                  : branch
              ),
            };
          }
        );

        // Force toggle rerender by incrementing its version
        setToggleVersions((prev) => ({
          ...prev,
          [variables.id]: (prev[variables.id] || 0) + 1,
        }));
      }

      // Clear stored original status
      setOriginalBranchStatus((prev) => {
        const updated = { ...prev };
        delete updated[variables.id];
        return updated;
      });

      const errorMessage = getPayMeErrorMessage(error, t);
      notify.error(errorMessage, { maxWidth: NOTIFICATION_MAX_WIDTH });
    },
  });

  const handleAddBranch = () => {
    setEditingBranch(null);
    setBranchNameApiError(null);
    setIsBranchNameModalOpen(true);
    amplitude.track(PAYME_EVENTS.PAYME_BRANCH_ADDITION_STARTED);
  };

  const handleEditBranch = (branch: Branch) => {
    setEditingBranch(branch);
    setBranchNameApiError(null);
    setIsBranchNameModalOpen(true);
  };

  const handleAddBranchConfirm = (branchName: string) => {
    setBranchNameApiError(null);
    createBranchMutation.mutate(branchName);
  };

  const handleUpdateBranchConfirm = (branchId: number, branchName: string) => {
    setBranchNameApiError(null);
    updateBranchNameMutation.mutate({ id: branchId, name: branchName });
  };

  const handleCloseBranchNameModal = () => {
    setIsBranchNameModalOpen(false);
    setEditingBranch(null);
    setBranchNameApiError(null); // Clear API error when closing modal
    // Reset mutation state when closing modal
    createBranchMutation.reset();
    updateBranchNameMutation.reset();
  };

  const handleBranchStatusChange = (branchId: number, isActive: boolean) => {
    // Only update if branches have been initialized (prevent initial onChange events)
    if (!branchesInitializedRef.current) {
      return;
    }
    // Only update if the value actually changed
    const branch = branches.find((b) => b.id === branchId);
    if (branch && branch.isActive === isActive) {
      return; // No change, skip API call
    }

    // If deactivating (going from true to false), show confirmation modal
    if (branch && branch.isActive && !isActive) {
      setBranchToDeactivate(branchId);
      return;
    }

    // If activating, proceed directly
    updateBranchStatusMutation.mutate({ id: branchId, isActive });
  };

  const handleConfirmDeactivateBranch = () => {
    if (branchToDeactivate === null) return;
    updateBranchStatusMutation.mutate({
      id: branchToDeactivate,
      isActive: false,
    });
    setBranchToDeactivate(null);
  };

  const handleCancelDeactivateBranch = () => {
    if (branchToDeactivate !== null) {
      // Force toggle to revert by incrementing version key for this branch
      setToggleVersions((prev) => ({
        ...prev,
        [branchToDeactivate]: (prev[branchToDeactivate] || 0) + 1,
      }));
    }
    setBranchToDeactivate(null);
  };

  const handleShare = (branch: Branch) => {
    setSelectedBranch(branch);
    setIsShareModalOpen(true);
  };

  const getBranchLink = (branch: Branch) => {
    if (!handle) return '';
    // Use the current name from local state if available, otherwise use branch.name
    const branchName = branchNames[branch.id] ?? branch.name;
    if (!branchName) return '';
    const branchParam = encodeURIComponent(branchName);
    return `${PAYME_BASE_URL}/${handle}?branch=${branchParam}`;
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedBranch(null);
  };

  useEffect(() => {
    return () => {
      // Ensure scroll is restored when component unmounts
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <div className={styles['branch-links-card']}>
        {branches.length > 0 && (
          <h2 className={styles['branch-links-card__title']}>
            {t('innerLayout.paymePage.branchLinks.title')}
          </h2>
        )}

        {isLoadingBranches && (
          <>
            <div className={styles['branch-links-card__skeleton-title']}>
              <SkeletonBase height="1.5rem" width="10rem" />
            </div>
            {[1, 2].map((index) => (
              <div key={index} className={styles['branch-links-card__branch']}>
                <div className={styles['branch-links-card__branch-row']}>
                  <div className={styles['branch-links-card__branch-name']}>
                    <div
                      className={styles['branch-links-card__skeleton-label']}
                    >
                      <SkeletonBase height="1.25rem" width="5rem" />
                    </div>
                    <SkeletonBase height="2.5rem" />
                  </div>
                  <div className={styles['branch-links-card__branch-link']}>
                    <div
                      className={styles['branch-links-card__skeleton-label']}
                    >
                      <SkeletonBase height="1.25rem" width="8rem" />
                    </div>
                    <SkeletonBase height="2.5rem" />
                  </div>
                  <div className={styles['branch-links-card__branch-status']}>
                    <div
                      className={styles['branch-links-card__skeleton-label']}
                    >
                      <SkeletonBase height="1.25rem" width="4rem" />
                    </div>
                    <div
                      className={styles['branch-links-card__toggle-wrapper']}
                    >
                      <SkeletonBase height="1.5rem" width="3rem" />
                      <SkeletonBase height="1rem" width="4rem" />
                    </div>
                  </div>
                  <div className={styles['branch-links-card__branch-share']}>
                    <SkeletonBase height="2.5rem" width="6rem" />
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {isErrorBranches && (
          <div>
            {t('innerLayout.paymePage.branchLinks.error')}
          </div>
        )}

        {!isLoadingBranches &&
          !isErrorBranches &&
          branches.map((branch) => {
            const isUpdatingName = updatingBranchId === branch.id;
            const isUpdatingStatus = updatingStatusBranchIds.has(branch.id);
            const isUpdating = isUpdatingName || isUpdatingStatus;

            // Show skeleton when status update is pending
            if (isUpdatingStatus) {
              return (
                <div
                  key={branch.id}
                  className={styles['branch-links-card__branch']}
                >
                  <div className={styles['branch-links-card__branch-row']}>
                    <div className={styles['branch-links-card__branch-name']}>
                      <div
                        className={styles['branch-links-card__skeleton-label']}
                      >
                        <SkeletonBase height="1.25rem" width="5rem" />
                      </div>
                      <SkeletonBase height="2.5rem" />
                    </div>
                    <div className={styles['branch-links-card__branch-link']}>
                      <div
                        className={styles['branch-links-card__skeleton-label']}
                      >
                        <SkeletonBase height="1.25rem" width="8rem" />
                      </div>
                      <SkeletonBase height="2.5rem" />
                    </div>
                    <div className={styles['branch-links-card__branch-status']}>
                      <div
                        className={styles['branch-links-card__skeleton-label']}
                      >
                        <SkeletonBase height="1.25rem" width="4rem" />
                      </div>
                      <div
                        className={styles['branch-links-card__toggle-wrapper']}
                      >
                        <SkeletonBase height="1.5rem" width="3rem" />
                        <SkeletonBase height="1rem" width="4rem" />
                      </div>
                    </div>
                    <div className={styles['branch-links-card__branch-share']}>
                      <SkeletonBase height="2.5rem" width="6rem" />
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={branch.id}
                className={styles['branch-links-card__branch']}
              >
                <div
                  className={`${styles['branch-links-card__branch-row']} ${isUpdatingName
                    ? styles['branch-links-card__branch-row--loading']
                    : ''
                    }`}
                >
                  {isUpdatingName && (
                    <div
                      className={styles['branch-links-card__loading-overlay']}
                    />
                  )}
                  <div className={styles['branch-links-card__branch-name']}>
                    <div
                      className={
                        styles['branch-links-card__branch-name-wrapper']
                      }
                    >
                      <Input
                        id={`branch-name-${branch.id}`}
                        name={`branch-name-${branch.id}`}
                        type="text"
                        value={branchNames[branch.id] ?? branch.name}
                        onChange={() => { }}
                        placeholder={t(
                          'innerLayout.paymePage.branchLinks.branchNamePlaceholder'
                        )}
                        trailingIcon={canChangeBranchHandle ? Pen : undefined}
                        label={t(
                          'innerLayout.paymePage.branchLinks.branchNameLabel'
                        )}
                        disabled={
                          updatingBranchId === branch.id ||
                          updatingStatusBranchIds.has(branch.id) ||
                          isPayMeConfigLoading
                        }
                        readOnly
                      />
                      {canChangeBranchHandle && (
                        <button
                          type="button"
                          className={
                            styles['branch-links-card__edit-icon-button']
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (
                              !updatingBranchId &&
                              updatingStatusBranchIds.size === 0
                            ) {
                              handleEditBranch(branch);
                            }
                          }}
                          disabled={
                            updatingBranchId === branch.id ||
                            updatingStatusBranchIds.has(branch.id) ||
                            isPayMeConfigLoading
                          }
                          aria-label={t(
                            'innerLayout.paymePage.branchLinks.editButton'
                          )}
                        />
                      )}
                    </div>
                  </div>
                  <div className={styles['branch-links-card__branch-link']}>
                    <Input
                      id={`branch-link-${branch.id}`}
                      name={`branch-link-${branch.id}`}
                      type="text"
                      value={getBranchLink(branch)}
                      onChange={() => { }}
                      readOnly
                      disabled
                      placeholder={t(
                        'innerLayout.paymePage.branchLinks.branchLinkPlaceholder'
                      )}
                      label={t(
                        'innerLayout.paymePage.branchLinks.branchLinkLabel'
                      )}
                    />
                    <div className={styles['branch-links-card__copy-button']}>
                      <CopyText textToCopy={getBranchLink(branch)} />
                    </div>
                  </div>
                  {canChangeBranchHandle && (
                    <div className={styles['branch-links-card__branch-status']}>
                      <div
                        className={styles['branch-links-card__status-wrapper']}
                      >
                        <label
                          className={styles['branch-links-card__status-label']}
                        >
                          {t('innerLayout.paymePage.branchLinks.statusLabel')}
                        </label>
                        <div
                          className={styles['branch-links-card__toggle-wrapper']}
                        >
                          <Toggle
                            key={`toggle-${branch.id}-${toggleVersions[branch.id] || 0
                              }`}
                            checked={branch.isActive}
                            onChangeHandler={(checked: boolean) =>
                              handleBranchStatusChange(branch.id, checked)
                            }
                            disabled={
                              updatingStatusBranchIds.has(branch.id) ||
                              !isMainHandleActivated ||
                              isPayMeConfigLoading
                            }
                          />
                          <span
                            className={styles['branch-links-card__status-value']}
                          >
                            {branch.isActive
                              ? t('innerLayout.paymePage.branchLinks.active')
                              : t('innerLayout.paymePage.branchLinks.inactive')}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {canSharePaymentHandle && (
                    <div className={styles['branch-links-card__branch-share']}>
                      <Button
                        id={`branch-share-${branch.id}`}
                        variant="contained"
                        color="primary"
                        size="md"
                        leadingIcon={ArrowUpRightFromSquare}
                        label={t('innerLayout.paymePage.branchLinks.shareButton')}
                        onClickHandler={() => handleShare(branch)}
                        disabled={!branch.name || !handle || !branch.isActive}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

        {canAddBranchHandle && (
          <div
            className={`${styles['branch-links-card__add-section']} ${branches.length === 0
              ? styles['branch-links-card__add-section--no-branches']
              : ''
              }`}
          >
            <div className={styles['branch-links-card__add-button']}>
              <Button
                id="add-branch-button"
                variant="link"
                color="primary"
                size="md"
                leadingIcon={Plus}
                label={t('innerLayout.paymePage.branchLinks.addBranchButton')}
                onClickHandler={handleAddBranch}
              />
            </div>
            <p className={styles['branch-links-card__add-description']}>
              {t('innerLayout.paymePage.branchLinks.addBranchDescription')}
            </p>
          </div>
        )}
      </div>

      {isShareModalOpen && selectedBranch && (
        <PayMeShareModal
          open={isShareModalOpen}
          onClose={handleCloseShareModal}
          paymeLink={getBranchLink(selectedBranch)}
          handleName={handle}
          branch={branchNames[selectedBranch.id] ?? selectedBranch.name}
          logoUrl={logoUrl}
        />
      )}

      <BranchNameModal
        open={isBranchNameModalOpen}
        onClose={handleCloseBranchNameModal}
        onAdd={handleAddBranchConfirm}
        onUpdate={handleUpdateBranchConfirm}
        branch={editingBranch}
        isUpdateBranchLoading={
          createBranchMutation.isPending ||
          updateBranchNameMutation.isPending
        }
        apiError={branchNameApiError}
        onApiErrorClear={() => setBranchNameApiError(null)}
      />

      {/* Deactivate Confirmation Modal */}
      <DeactivateConfirmModal
        open={branchToDeactivate !== null}
        onClose={handleCancelDeactivateBranch}
        onConfirm={handleConfirmDeactivateBranch}
        isBranch={true}
        isLoading={updatingStatusBranchIds.has(branchToDeactivate || -1)}
      />

      
    </>
  );
};
